import { Box, IconButton } from "@mui/material";
import { forwardRef, type ReactElement } from "react";

import { type Corner } from "../types";

interface FloatingButtonProps {
  position: Corner;
  isDragging: boolean;
  onMouseDown: (event: React.MouseEvent | React.TouchEvent) => void;
  onTouchStart: (event: React.MouseEvent | React.TouchEvent) => void;
  onClick: () => void;
}

const getPositionStyles = (position: Corner) => {
  const spacing = 12; // Base spacing

  switch (position) {
    case "top-left": {
      return {
        top: `max(${spacing}px, env(safe-area-inset-top) + 8px)`,
        left: `max(${spacing}px, env(safe-area-inset-left) + 8px)`,
      };
    }

    case "top-right": {
      return {
        top: `max(${spacing}px, env(safe-area-inset-top) + 8px)`,
        right: `max(${spacing}px, env(safe-area-inset-right) + 8px)`,
      };
    }

    case "bottom-left": {
      return {
        bottom: `max(${spacing}px, env(safe-area-inset-bottom) + 8px)`,
        left: `max(${spacing}px, env(safe-area-inset-left) + 8px)`,
      };
    }

    default: {
      // bottom-right
      return {
        bottom: `max(${spacing}px, env(safe-area-inset-bottom) + 8px)`,
        right: `max(${spacing}px, env(safe-area-inset-right) + 8px)`,
      };
    }
  }
};

export const FloatingButton = forwardRef<HTMLButtonElement, FloatingButtonProps>(
  ({ position, isDragging, onMouseDown, onTouchStart, onClick }, ref): ReactElement => {
    return (
      <Box
        sx={{
          position: "fixed",
          ...getPositionStyles(position),
          zIndex: 9999,
        }}
      >
        <IconButton
          ref={ref}
          sx={{
            width: 48,
            height: 48,
            backgroundColor: "rgba(255, 255, 255, 0.95)",
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.3), 0px 2px 8px rgba(0, 0, 0, 0.2)",
            cursor: isDragging ? "grabbing" : "grab",
            "&:hover": {
              transform: isDragging ? "none" : "scale(1.05)",
            },
            transition: isDragging ? "none" : "all 0.2s ease-in-out",
            userSelect: "none",
            padding: 0,
          }}
          onMouseDown={onMouseDown}
          onTouchStart={onTouchStart}
          onClick={onClick}
        >
          <img
            src="/assets/logo/bunny.png"
            alt="Debug Console"
            style={{
              width: "70%",
              height: "70%",
              borderRadius: "50%",
            }}
          />
        </IconButton>
      </Box>
    );
  }
);

FloatingButton.displayName = "FloatingButton";
